import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
	AlertIcon,
	CloseThin,
	DropDown,
	LogoSmall,
	PaperAddGreen,
	PausedIcon,
	PlayIcon,
	SentIcon,
	TrashOrange
} from '@/assets/images';
import Button from '@/components/Button/Button';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import HorizontalDotsButton from '@/components/HorizontalDotsButton/HorizontalDotsButton';
import CheckboxWithIcon from '@/components/CheckboxWithIcon/ChecboxWithIcon';
import Dialog from '@/components/Dialog/Dialog';
import { bulkRemoveBusinessesFromSequence } from '@/services/sequencesService';
import CroppedText from '@/components/CroppedText/CroppedText';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import Loader from '@/components/Loader/Loader';
import Tooltip from '@/components/Tooltip/Tooltip';
import { sendAnalytics } from '@/services/analyticsService';
import { toast } from 'react-hot-toast';
import { BusinessTableContainer } from '../../scenes/SequenceAddBusinessPage';
import InfiniteScroll from '../InfiniteScroll';
import SequencesStepsList from '../StepsList';
import { useBusinesses } from '../../hooks/useBusinesses';
import { SearchIconNew } from './SequencesContainerHeader';
import { useSequences, type BusinessType } from '../../hooks/useSequences';
import { useSequencesContext } from '../../SequencesContext';

export default function StepsContainer() {
	const { activeTab } = useSequencesContext();

	return (
		<>
			{activeTab === 'SEQUENCE' && <SequenceSteps />}
			{activeTab === 'BUSINESSES' && <BusinessContainer />}
		</>
	);
}

function SequenceSteps() {
	const { selectedSequence } = useSequencesContext();

	const steps = selectedSequence?.steps;

	return <SequencesStepsList steps={steps} />;
}

function BusinessContainer() {
	const { selectedSequence, isEnrollOpen, setIsEnrollOpen } =
		useSequencesContext();
	const { fetchBusinesses, isLoading, firstLoading, setFirstLoading } =
		useBusinesses();
	const [searchingBusiness, setSearchingBusiness] = useState(false);
	const [search, setSearch] = useState('');
	const [isDeletingBusinesses, setIsDeletingBusinesses] = useState(false);
	const [businessToDelete, setBusinessToDelete] = useState<
		string | undefined
	>();
	const [selectedBusinessesIds, setSelectedBusinessesIds] = useState<
		string[]
	>([]);
	const usedFilteredSearch = useRef(false);

	useEffect(() => {
		fetchBusinesses({ first: true });

		return () => {
			setIsEnrollOpen(false);
			setIsDeletingBusinesses(false);
			setBusinessToDelete(undefined);
			setSelectedBusinessesIds([]);
		};
	}, []);

	function onEnrollOpen() {
		setIsEnrollOpen(!isEnrollOpen);
	}

	function setSelected(business: BusinessType) {
		const isSelected = selectedBusinessesIds.includes(business.id);

		if (isSelected) {
			setSelectedBusinessesIds(
				selectedBusinessesIds.filter(id => id !== business.id)
			);
			return;
		}
		setSelectedBusinessesIds(prev => [...prev, business.id]);
	}

	function setAllBusinessesSelected() {
		if (
			selectedBusinessesIds.length ===
			selectedSequence?.businesses?.length
		) {
			setSelectedBusinessesIds([]);
		} else {
			setSelectedBusinessesIds(
				selectedSequence?.businesses?.map(b => b.id) || []
			);
		}
	}

	function handleEnrollBusinessesClick() {
		onEnrollOpen();
	}

	const businessCount = useRef(0);
	const businessLength = useRef(0);
	useEffect(() => {
		businessCount.current =
			selectedSequence?.analytics?.totalBusinessCount || 0;
		businessLength.current = selectedSequence?.businesses?.length || 0;
	}, [selectedSequence]);

	function onIntersect() {
		if (
			businessCount.current > businessLength.current &&
			!usedFilteredSearch.current
		) {
			fetchBusinesses();
		}
	}

	function handleIsDeleting() {
		if (isDeletingBusinesses) {
			setBusinessToDelete(undefined);
		}
		setIsDeletingBusinesses(!isDeletingBusinesses);
	}

	function handleBlur() {
		if (search.length === 0 && searchingBusiness) {
			usedFilteredSearch.current = false;
			setFirstLoading(true);
			fetchBusinesses({ first: true });
		}

		setSearchingBusiness(false);
	}

	function handleFocus() {
		setSearchingBusiness(true);
	}

	function handleSubmitSearch(e: React.FormEvent) {
		e.preventDefault();
		if (search.length === 0) return;

		usedFilteredSearch.current = true;
		setFirstLoading(true);
		fetchBusinesses({ first: true, search });
	}

	const isSearchBoxOpen = searchingBusiness || search.length > 0;

	return (
		<div className="flex h-full flex-col px-4">
			<EnrollBusinesses
				isEnrollOpen={isEnrollOpen}
				onEnrollOpen={onEnrollOpen}
			/>
			<div
				style={{
					height: '12%'
				}}
				className="flex h-full w-full items-center border-b border-dashed border-dark-grey px-4"
			>
				<div
					style={{
						transition: 'width 0.5s ease'
					}}
					className={`mr-4 ${!isSearchBoxOpen ? 'w-48' : 'w-16'} h-9`}
				>
					<Button
						variant="secondary"
						leftIcon={!isSearchBoxOpen && <PaperAddGreen />}
						className="hover:bg-orange hover:text-black"
						onClick={handleEnrollBusinessesClick}
					>
						<span
							className={`${
								isSearchBoxOpen ? 'hidden' : 'visible'
							}`}
						>
							Enroll businesses
						</span>
						{isSearchBoxOpen && <PaperAddGreen />}
					</Button>
				</div>
				<DeleteSelectedBusinessesButton
					isSearchBoxOpen={isSearchBoxOpen}
					handleOpen={handleIsDeleting}
					selectedBusinessesIds={selectedBusinessesIds}
				/>
				<DeleteBusinessesDialog
					open={isDeletingBusinesses}
					handleOpen={handleIsDeleting}
					businessId={businessToDelete}
					selectedBusinessesIds={selectedBusinessesIds}
					setSelectedBusinessesIds={setSelectedBusinessesIds}
				/>
				<div
					style={{
						transition: 'width 0.5s ease'
					}}
					className={`ml-auto flex h-9 items-end gap-4 ${
						isSearchBoxOpen ? 'w-2/3' : 'w-48'
					}`}
				>
					<form
						onSubmit={handleSubmitSearch}
						className="relative h-full w-full"
					>
						<input
							type="text"
							value={search}
							onChange={e => setSearch(e.target.value)}
							onFocus={handleFocus}
							onBlur={handleBlur}
							placeholder={
								isSearchBoxOpen ? 'Search a business' : ''
							}
							className="mr-4 h-full w-full rounded-md bg-dm-black px-5 py-2"
						/>
						<button
							style={{
								transition: 'right 0.5s ease'
							}}
							className={`absolute bottom-0 top-0 mx-auto my-auto flex w-7 items-center ${
								isSearchBoxOpen
									? 'right-2'
									: 'pointer-events-none right-9'
							} text-orange`}
						>
							<SearchIconNew />
						</button>
					</form>
					<span className="w-28 whitespace-nowrap text-right">
						{selectedSequence?.businesses?.length || 0} results
					</span>
				</div>
			</div>
			{/* BusinessList */}
			<div
				style={{
					height: '85%'
				}}
				className="overflow-hidden rounded-b-lg"
			>
				{isLoading && firstLoading ? (
					<div className="flex h-full items-center justify-center">
						<Loader />
					</div>
				) : (
					<>
						{(selectedSequence?.businesses?.length || 0) === 0 && (
							<div className="flex h-full items-center justify-center">
								<span className="text-center text-dark-grey">
									There are no businesses here. <br />
									Try enrolling new businesses to get started.
								</span>
							</div>
						)}
						<table
							style={{ height: '100%' }}
							className="flex flex-col text-left text-sm text-gray-200"
						>
							<BusinessListHeader
								setAllBusinessesSelected={
									setAllBusinessesSelected
								}
								allSelected={
									selectedBusinessesIds.length ===
									selectedSequence?.businesses?.length
								}
							/>
							<tbody className="flex h-full flex-col overflow-y-auto">
								{selectedSequence?.businesses?.map(
									(business, index) => (
										<BusinessListItem
											key={business.id + index}
											index={index}
											business={business}
											selected={selectedBusinessesIds.includes(
												business.id
											)}
											setSelected={setSelected}
											handleIsDeleting={handleIsDeleting}
											setBusinessToDelete={
												setBusinessToDelete
											}
										/>
									)
								)}
								<tr>
									<td>
										<InfiniteScroll
											onIntersect={onIntersect}
										/>
									</td>
								</tr>
								{isLoading && !firstLoading && (
									<tr className="flex h-8 items-center justify-center">
										<td>
											<Loader className="w-5" />
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</>
				)}
			</div>
		</div>
	);
}

function DeleteSelectedBusinessesButton({
	isSearchBoxOpen,
	selectedBusinessesIds,
	handleOpen
}: {
	isSearchBoxOpen: boolean;
	selectedBusinessesIds: string[];
	handleOpen: () => void;
}) {
	return (
		<>
			<div
				style={{
					transition: 'width 0.5s ease'
				}}
				className={`${!isSearchBoxOpen ? 'w-48' : 'w-16'} h-9`}
			>
				{selectedBusinessesIds.length > 0 && (
					<Button
						variant="secondary"
						leftIcon={!isSearchBoxOpen && <TrashOrange />}
						className="hover:bg-orange hover:text-black"
						onClick={handleOpen}
						disabled={selectedBusinessesIds.length === 0}
					>
						<span
							className={`${
								isSearchBoxOpen ? 'hidden' : 'visible'
							}`}
						>
							Delete selected
						</span>
						{isSearchBoxOpen && <TrashOrange />}
					</Button>
				)}
			</div>
		</>
	);
}

function DeleteBusinessesDialog({
	open,
	handleOpen,
	businessId,
	selectedBusinessesIds,
	setSelectedBusinessesIds
}: {
	open: boolean;
	handleOpen: () => void;
	businessId?: string;
	selectedBusinessesIds?: string[];
	setSelectedBusinessesIds: (x: string[]) => void;
}) {
	const {
		selectedSequence,
		setSelectedBusinesses,
		businessesAlreadyInSequence,
		setBusinessesAlreadyInSequence
	} = useSequencesContext();
	const { fetchBusinesses } = useBusinesses();
	const { fetchSequences } = useSequences();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const onConfirmDelete = () => {
		if (!selectedSequence || !selectedSequence.id) return;
		const businessesIdsList = businessId
			? [businessId]
			: selectedBusinessesIds || [];
		bulkRemoveBusinessesFromSequence({
			campaignId: selectedSequence.id,
			businesses: businessesIdsList
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error deleting businesses! Please try again later.'
					);
				}
				fetchBusinesses({ first: true });
				fetchSequences();
				setSelectedBusinesses([]);
				if (!businessId) {
					setSelectedBusinessesIds([]);
				}
				toast.success('Businesses deleted successfully!');
			})
			.catch(err => {
				toast.error(err.message);
			})
			.finally(() => {
				handleOpen();
			});

		businessesIdsList.forEach(businessId => {
			const isInSequence = businessesAlreadyInSequence.has(businessId);
			if (!isInSequence) return;
			setBusinessesAlreadyInSequence(
				// @ts-ignore
				prev => new Set(prev.delete(businessId))
			);
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			deletedCount: businessesIdsList.length,
			eventName: 'user_unenrolled_businesses_sequences'
		});
	};

	return (
		<Dialog isOpen={open} handleOpen={handleOpen}>
			<span className="mb-2 text-2xl font-medium text-orange">
				Confirm delete
			</span>
			<span className="text-lg text-gray-200">
				Are you sure you want to remove the selected businesses from the
				sequence?
			</span>
			<div className="flex justify-between">
				<div className="w-1/3">
					<Button variant="ghost" onClick={handleOpen}>
						Cancel
					</Button>
				</div>
				<div className="w-2/5">
					<Button variant="danger" onClick={onConfirmDelete}>
						Confirm Delete
					</Button>
				</div>
			</div>
		</Dialog>
	);
}

function BusinessListHeader({
	setAllBusinessesSelected,
	allSelected
}: {
	setAllBusinessesSelected: () => void;
	allSelected: boolean;
}) {
	return (
		<thead className="text-xs">
			<tr className="flex items-center justify-between">
				<th className="p-4" scope="col">
					<div className="h-6 w-6">
						<CheckboxWithIcon
							checked={allSelected}
							onToggle={setAllBusinessesSelected}
							color="yellow"
						/>
					</div>
				</th>
				<th
					className="w-20 flex-shrink-0 font-medium text-orange"
					scope="col"
				>
					List origin
				</th>
				<th className="ml-2 w-32 flex-shrink-0 font-medium" scope="col">
					Name
				</th>
				<th className="w-full text-center font-medium" scope="col">
					RE2 Score
				</th>
				<th className="w-full text-center font-medium" scope="col">
					Categories
				</th>
				<th className="w-full text-center font-medium" scope="col">
					Location
				</th>
				<th className="w-full text-center font-medium" scope="col">
					Step
				</th>
				<th className="w-1/2" scope="col" />
			</tr>
		</thead>
	);
}

function BusinessListItem({
	business,
	index,
	selected,
	setSelected,
	handleIsDeleting,
	setBusinessToDelete
}: {
	business: BusinessType;
	index: number;
	selected: boolean;
	setSelected: (business: BusinessType) => void;
	handleIsDeleting: () => void;
	setBusinessToDelete: (business: string) => void;
}) {
	const { retryInitiateBusinesses } = useSequences();

	const options = [
		{
			id: '1',
			label: 'Remove from sequence',
			onClick: () => {
				handleIsDeleting();
				setBusinessToDelete(business.id);
			}
		},
		...(business.status === 'HALTED'
			? [
					{
						id: '2',
						label: 'Resend all failed emails',
						onClick: () => {
							retryInitiateBusinesses();
						}
					}
				]
			: [])
	];

	return (
		<tr
			className={`flex items-center justify-between ${index % 2 === 0 ? 'bg-dm-black3' : 'bg-dm-black'} `}
		>
			<td className="p-4">
				<div className="h-6 w-6">
					<CheckboxWithIcon
						className="individual-business-checkbox-sequences"
						checked={selected}
						onToggle={() => setSelected(business)}
					/>
				</div>
			</td>
			<td className="w-20 flex-shrink-0">
				<CroppedText text={business.listOriginName} maxLength={10} />
			</td>
			<td className="ml-2 w-32 flex-shrink-0">
				<CroppedText text={business.name} maxLength={20} />
			</td>
			<td className="w-full text-center">
				{Math.floor(business.score || 0)}
			</td>
			<td className="w-full text-center">
				<CroppedText text={business.categories} maxLength={19} />
			</td>
			<td className="w-full text-center">{business.locations}</td>
			<td className="flex h-full w-full items-center justify-center text-center">
				<div
					style={{ height: '60%' }}
					className="flex h-full items-center rounded-md bg-dm-black3.5"
				>
					<span className="w-12 border-r border-dark-grey">
						{business.step}
					</span>
					<div className="x- relative flex w-12 items-center justify-center">
						{business.status === 'ACTIVE' && (
							<Tooltip
								content="This sequence is currently in progress."
								longerBubble={false}
								position="left"
								contentClassName="-translate-x-40"
							>
								<PlayIcon />
							</Tooltip>
						)}
						{business.status === 'COMPLETED' && (
							<Tooltip
								content="All planned activities for this sequence have been completed."
								longerBubble={false}
								position="left"
								contentClassName="-translate-x-40"
							>
								<SentIcon />
							</Tooltip>
						)}
						{business.status === 'HALTED' && (
							<Tooltip
								content={String(business.failReason)}
								longerBubble={false}
								position="left"
								contentClassName="-translate-x-40"
							>
								<AlertIcon />
							</Tooltip>
						)}
						{business.status === 'REPLIED' && (
							<Tooltip
								content="This sequence has been stopped because the business has replied."
								longerBubble={false}
								position="left"
								contentClassName="-translate-x-40"
							>
								<PausedIcon />
							</Tooltip>
						)}
					</div>
				</div>
			</td>
			<td className="relative mr-4 w-min">
				<DropdownMenu
					options={options}
					itemClassName="py-3 px-2 hover:bg-dm-black3"
				>
					<HorizontalDotsButton />
				</DropdownMenu>
				{business.status === 'HALTED' && (
					<span className="absolute -top-1 right-1 text-xl text-danger">
						*
					</span>
				)}
			</td>
		</tr>
	);
}

function EnrollBusinesses({
	isEnrollOpen,
	onEnrollOpen
}: {
	isEnrollOpen: boolean;
	onEnrollOpen: () => void;
}) {
	const [lists, monthlyLimit] = useSelector(
		(state: RootState) => [
			state.dashboard.getIn(['lists', 'data']).toJS(),
			state.email.getIn(['email', 'monthlyLimit'])
		],
		shallowEqual
	);

	const {
		selectedLists,
		setSelectedLists,
		setShowSendAll,
		selectedBusinesses,
		selectedSequence,
		unapprovedBusinesses,
		unselectedBusinesses
	} = useSequencesContext();

	function setSendAllOpen() {
		onEnrollOpen();
		setShowSendAll(true);
	}
	const businessesInSelectedListsCount = selectedLists.reduce(
		(count, list) => count + list.businessIds.length,
		0
	);

	const hasSelectedList = selectedLists.length > 0;

	const displayReviewButton =
		hasSelectedList && selectedBusinesses.length > 0;

	const selectedListName = selectedLists[0]?.name;

	const willSequenceProceed = useMemo(() => {
		const currentSequenceLength = selectedSequence?.businesses?.length || 0;
		if (unselectedBusinesses[0] === 'none') {
			const value =
				currentSequenceLength +
				(businessesInSelectedListsCount - unselectedBusinesses.length);
			return monthlyLimit >= value;
		}
		if (unselectedBusinesses[0] === 'all') {
			const value =
				currentSequenceLength +
				(selectedBusinesses.length - unselectedBusinesses.length);
			return monthlyLimit >= value;
		}
	}, [
		unselectedBusinesses,
		selectedSequence,
		businessesInSelectedListsCount,
		selectedBusinesses,
		unapprovedBusinesses,
		monthlyLimit
	]);

	const exceededBy = useMemo(() => {
		const currentSequenceLength = selectedSequence?.businesses?.length || 0;
		if (unselectedBusinesses[0] === 'none') {
			return (
				currentSequenceLength +
				(businessesInSelectedListsCount - unselectedBusinesses.length)
			);
		}
		if (unselectedBusinesses[0] === 'all') {
			return (
				currentSequenceLength +
				(selectedBusinesses.length - unselectedBusinesses.length)
			);
		}
		return 0;
	}, [
		unselectedBusinesses,
		selectedSequence,
		businessesInSelectedListsCount,
		selectedBusinesses,
		unapprovedBusinesses
	]);

	return (
		<Dialog
			isOpen={isEnrollOpen}
			handleOpen={onEnrollOpen}
			width="1200px"
			height="80vh"
		>
			<div className="flex h-full flex-col gap-3 text-white">
				<div className="flex items-start justify-between">
					<span className="text-lg font-medium">
						Enroll businesses
					</span>
					<button onClick={onEnrollOpen}>
						<CloseThin />
					</button>
				</div>
				<div className="border-t border-dark-grey" />
				<div className="flex items-center justify-between">
					<div className="w-min whitespace-nowrap rounded-md border border-dark-grey py-2 text-gray-200">
						<DropdownMenu
							options={lists.map((list: any) => ({
								id: list.id,
								label: list.name,
								onClick: () => {
									setSelectedLists([list]);
								}
							}))}
							position="bottom"
							itemClassName="bg-dm-black3.5 border-b border-dark-grey hover:bg-orange hover:text-black px-4 py-3 text-lg"
						>
							<div className="flex h-full w-full items-center justify-between gap-4 px-4 text-gray-200">
								<span className="w-52 overflow-hidden text-left">
									{selectedListName ||
										'Select a List you want to enroll'}
								</span>
								<DropDown />
							</div>
						</DropdownMenu>
					</div>
					{displayReviewButton && (
						<div className="h-full w-36">
							<Button
								onClick={() => {
									if (willSequenceProceed == false) {
										toast.error(
											`You have exceeded the max number of businesses you can enroll at this time. Remove ${exceededBy} businesses to get to your limit of ${monthlyLimit} businesses to continue`,
											{ position: 'top-right' }
										);
										return;
									}

									setSendAllOpen();
								}}
							>
								Review
							</Button>
						</div>
					)}
				</div>
				{hasSelectedList ? (
					<BusinessTableContainer />
				) : (
					<NoListSelectedTooltip />
				)}
			</div>
		</Dialog>
	);
}

function NoListSelectedTooltip() {
	return (
		<div className="h-full rounded-xl border border-dark-grey bg-dm-black3 p-12">
			<div className="flex flex-col gap-9">
				<div className="flex items-center gap-5">
					<LogoSmall />
					<h6 className="text-2xl font-medium text-gray-200">
						Would you like to add a business to your sequence?
					</h6>
				</div>
				<span className="text-light-grey">
					You can start by selecting one list. All businesses in it
					will be automatically selected in your sequence.
					<br />
					<br />
					You can uncheck any businesses you don't want to include.{' '}
					<br />
					<br />
					In this step, you can enroll all the businesses from one
					list at a time.
				</span>
			</div>
		</div>
	);
}
